import type {
    ContentJsonObject,
    EnrichedFiltersContentJsonObject,
    EnrichedSignalsContentJsonObject,
    ErrorJsonObject,
    FiltersContentJsonObject,
    ParsedContentCommand,
    SignalsContentJsonObject,
    CraftedSignal,
    ExistingFilterSet,
} from "./content";

const COMMAND_TYPE_JSON: ReadonlySet<ParsedContentCommand["name"]> = new Set([
    "OutputFilters",
    "ContextualSuggestions",
    "SuggestedQueries",
    "SignalsFiltersWereAdded",
    "SignalsFiltersWereModified",
    "SignalsFiltersWereRemoved",
    "CraftedSignal",
]);

const FILTERS_TYPE_JSON: ReadonlySet<FiltersContentJsonObject["name"]> = new Set([
    "FiltersWereAdded",
    "FiltersWereModified",
    "FiltersWereRemoved",
]);

const SIGNALS_TYPE_JSON: ReadonlySet<SignalsContentJsonObject["name"]> = new Set([
    "SignalsFiltersWereAdded",
    "SignalsFiltersWereModified",
    "SignalsFiltersWereRemoved",
]);

export const isContentJsonObject = (value: unknown): value is ContentJsonObject => {
    if (typeof value !== "object" || !value) {
        return false;
    }

    return "name" in value && "data" in value;
};

export const isErrorJsonObject = (value: unknown): value is ErrorJsonObject => {
    if (typeof value !== "object" || !value) {
        return false;
    }

    return "name" in value && !("data" in value) && "code" in value;
};

export const isParsedContentCommand = (value: unknown): value is ParsedContentCommand => {
    if (isContentJsonObject(value)) {
        return COMMAND_TYPE_JSON.has(value.name as ParsedContentCommand["name"]);
    }

    return false;
};

export const isSignalsContentJsonObject = (value: unknown): value is SignalsContentJsonObject => {
    if (isContentJsonObject(value)) {
        return SIGNALS_TYPE_JSON.has(value.name as SignalsContentJsonObject["name"]);
    }

    return false;
};

export const isDraftSignalJsonObject = (value: unknown): value is CraftedSignal => {
    if (isContentJsonObject(value)) {
        return value.name === "CraftedSignal";
    }

    return false;
};

export const isFiltersContentJsonObject = (value: unknown): value is FiltersContentJsonObject => {
    if (isContentJsonObject(value)) {
        return FILTERS_TYPE_JSON.has(value.name as FiltersContentJsonObject["name"]);
    }

    return false;
};

export const isExistingFilterSetJsonObject = (value: unknown): value is ExistingFilterSet => {
    if (isContentJsonObject(value)) {
        return value.name === "ExistingFilterSet";
    }

    return false;
};

export const isEnrichedFiltersContentJsonObject = (value: unknown): value is EnrichedFiltersContentJsonObject => {
    return isFiltersContentJsonObject(value);
};

export const isEnrichedSignalsContentJsonObject = (value: unknown): value is EnrichedSignalsContentJsonObject => {
    if (isSignalsContentJsonObject(value)) {
        return "signalsFilters" in value.data;
    }

    return false;
};
